// ── Blog Category Page (/blog/category/:slug) ────────────────────────────────
// Design: Indexed dark system — H1 title, 3-col grid, pagination

import { useState } from "react";
import { useParams } from "wouter";
import {
  getCategoryBySlug,
  getPostsByCategory,
  getAuthorBySlug,
  POSTS,
} from "@/lib/blogMeta.generated";
import SiteLayout from "@/components/SiteLayout";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import BlogPagination from "@/components/BlogPagination";
import { hasDiagram } from "@/components/blog-diagrams/index";
import { longReadLabel, postExcerpt, relativeDate, stripTitleSuffix } from "@/lib/blogText";
import CoverImage from "@/components/CoverImage";
import { SITE_URL } from "@/lib/brand";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const PER_PAGE = 9;

// Per-category hub intro (rendered under the H1) — every category has one so the
// hubs are consistent. JSX so we can embed one contextual tool/service link each.
const introLink = { color: "#57d7e2", textDecoration: "none", fontWeight: 600 } as const;
const CATEGORY_INTROS: Record<string, React.ReactNode> = {
  "website-authority": (
    <>
      Website authority is the trust and ranking power a domain has earned in the eyes of search engines — it's the single biggest reason one site outranks another for competitive terms. No search engine publishes an official authority score, so the industry relies on third-party proxies: Ahrefs' Domain Rating (DR), Moz's Domain Authority (DA), and Semrush's Authority Score. This hub explains what each metric measures, what counts as a good score, and the tactics that actually move it — from earning editorial links to closing the technical and content gaps that hold a site back. Start by checking where you stand today with our{" "}
      <a href="/tools/domain-rating-checker" style={introLink}>free domain rating checker tool</a>, then work through the playbooks below.
    </>
  ),
  "ai-seo": (
    <>
      AI SEO — sometimes called generative engine optimization (GEO) — is the practice of making your brand visible inside AI answers from ChatGPT, Perplexity, Gemini, and Google's AI Overviews, not just the ten blue links. These engines choose sources differently from classic search: they reward clear, well-structured, citable content and established entity authority. This hub covers how AI search decides what to cite, how to track your share of AI answers, and how to optimize for each engine. See where your brand stands today with our{" "}
      <a href="/tools/llm-visibility-checker" style={introLink}>free LLM visibility checker</a>, then work through the guides below.
    </>
  ),
  "claude-seo": (
    <>
      Claude SEO is the practical craft of using Anthropic's Claude across real SEO work — drafting and optimizing content, building briefs and topic clusters, generating schema, and triaging audits — while knowing exactly where it helps and where it quietly hurts. Claude is strong at structure, tone, and instruction-following, but it has no live search data and will invent statistics, so every output needs human verification. This hub collects copy-paste prompts, step-by-step workflows, and honest limits for each task. If you'd rather have a team run AI-assisted SEO end to end, explore our{" "}
      <a href="/ai-seo" style={introLink}>AI SEO services</a>, then work through the guides below.
    </>
  ),
  "seo-strategy": (
    <>
      SEO strategy is the decision layer that turns scattered tactics into compounding organic growth — choosing which keywords, pages, and links actually move revenue, and in what order. Without it, teams ship effort that never adds up. This hub covers how to audit a site, prioritize findings into an action plan, report on what matters to leadership, and build SEO that survives algorithm and AI-search shifts. If you'd rather have a team own this end to end, explore our{" "}
      <a href="/seo-audit-and-strategy" style={introLink}>SEO audit and strategy</a> service, then dig into the playbooks below.
    </>
  ),
  "technical-seo": (
    <>
      Technical SEO is the foundation that lets every other SEO effort work — making sure search engines can crawl, render, and index your site without friction. Crawl budget, site architecture, Core Web Vitals, redirects, canonicals, structured data, and hreflang all live here. Get these wrong and great content never ranks; get them right and the whole site compounds. This hub breaks down the audits and fixes that matter most, with checklists you can act on. For a hands-on review of your site, see our{" "}
      <a href="/technical-seo" style={introLink}>technical SEO services</a>, then explore the guides below.
    </>
  ),
  "content-seo": (
    <>
      Content SEO is how you research, create, and structure content that ranks in Google, converts readers, and gets cited by AI engines. It's the overlap between what your audience searches for and what your business needs to be known for. This hub covers keyword research, content briefs and audits, topical authority, and writing that performs in both classic and AI search. Whether you're building a content engine or fixing one that's stalled, start here — and if you need the work done for you, see our{" "}
      <a href="/content-production" style={introLink}>content production</a> service, then read on.
    </>
  ),
  "link-building": (
    <>
      Link building is how sites earn the authority that decides who ranks for competitive terms — but only the right links count. Editorial links from relevant, trusted domains move rankings; bought links and low-quality directories risk penalties. This hub covers what makes a backlink high quality, how digital PR earns links at scale, broken-link and outreach tactics, and the strategy that ties them together. Learn to build authority that lasts, not link-graph noise — and if you'd rather have it run for you, explore our{" "}
      <a href="/link-building" style={introLink}>link building services</a>, then work through the guides below.
    </>
  ),
  "local-seo": (
    <>
      Local SEO is how businesses win the map pack and "near me" searches that drive calls, visits, and bookings. It rewards a different signal set than national SEO: a strong Google Business Profile, consistent citations, local relevance, and reviews. This hub covers profile optimization, the ranking factors that move local results, citation building, and how to compete in your city. Whether you run one location or a hundred, start here — and if you want a team to own your local visibility, see our{" "}
      <a href="/local-seo" style={introLink}>local SEO services</a>, then explore the guides below.
    </>
  ),
  "programmatic-seo": (
    <>
      Programmatic SEO is how you capture long-tail demand at scale — generating hundreds or thousands of high-quality, templated pages from structured data instead of writing each by hand. Done well it builds durable organic traffic; done badly it creates thin, duplicate pages that get filtered out. This hub covers when programmatic SEO works, how to source and structure data, indexation strategy at scale, and the pitfalls that make it backfire. Learn to build pages that rank, not bloat — and if you want it built for you, see our{" "}
      <a href="/programmatic-seo" style={introLink}>programmatic SEO services</a>, then read on.
    </>
  ),
};

export default function BlogCategory() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState(1);

  const isLatest = slug === "latest";
  const category = isLatest ? null : getCategoryBySlug(slug ?? "");
  // Curated hub intro if we have one; otherwise fall back to the category's
  // description so auto-registered categories still get an intro paragraph.
  const intro: React.ReactNode = category ? (CATEGORY_INTROS[category.slug] ?? category.description) : null;
  const catTitle = isLatest ? "Latest Articles | Indexed Blog" : category ? `${category.label} Articles | Indexed Blog` : "Category | Indexed Blog";
  const catDesc = isLatest
    ? "Browse the latest SEO, AI search, and content strategy articles from the Indexed team."
    : category
    ? `Browse all ${category.label} articles on the Indexed blog — practical insights for growth-focused teams.`
    : "Browse articles on the Indexed blog.";
  const canonicalSlug = isLatest ? "/blog/category/latest" : category ? `/blog/category/${category.slug}` : "/blog";
  const listed = isLatest || category
    ? (isLatest ? [...POSTS] : getPostsByCategory(category!.slug))
        .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
    : [];
  // CollectionPage + ItemList so search engines read the hub as a list of
  // articles (found missing in the 2026-11-04 audit).
  const jsonLd = listed.length ? [{
    "@type": "CollectionPage",
    name: catTitle.replace(/\s*\|\s*Indexed Blog$/, ""),
    description: catDesc,
    url: `${SITE_URL}${canonicalSlug}`,
    isPartOf: { "@type": "Blog", name: "Indexed Blog", url: `${SITE_URL}/blog` },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: listed.length,
      itemListElement: listed.slice(0, 30).map((p, i) => ({
        "@type": "ListItem", position: i + 1, url: `${SITE_URL}/blog/${p.slug}`, name: stripTitleSuffix(p.title),
      })),
    },
  }] : undefined;
  useSeoMeta({ title: catTitle, description: catDesc, canonical: canonicalSlug, jsonLd });
  const isMobile = useIsMobile();

  if (!isLatest && !category) {
    return (
      <SiteLayout>
        <div style={{ background: "#0c0c0c", minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "120px 24px 80px" }}>
          <div style={{ textAlign: "center", maxWidth: "480px" }}>
            <h1 style={{ color: "white", fontSize: "clamp(28px, 4vw, 36px)", fontWeight: 700, fontFamily: ff, marginBottom: "12px" }}>Category not found</h1>
            <p style={{ color: "rgba(255,255,255,0.66)", fontSize: "16px", fontFamily: ff, lineHeight: 1.7, marginBottom: "32px" }}>This category doesn't exist. Browse all our articles below.</p>
            <a href="/blog" style={{ fontFamily: ff, fontSize: "15px", fontWeight: 600, color: "#0c0c0c", background: "#57d7e2", padding: "12px 28px", borderRadius: "6px", textDecoration: "none", display: "inline-block" }}>Browse the Blog</a>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const allPosts = listed;

  const displayLabel = isLatest ? "Latest" : category!.label;
  const accentColor = isLatest ? "#57d7e2" : category!.color;

  const totalPages = Math.ceil(allPosts.length / PER_PAGE);
  const pagePosts = allPosts.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <SiteLayout>
      <div style={{ background: "#0a0a0a", minHeight: "100vh" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "132px 48px 120px" }}>

          {/* Breadcrumb */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
            <a href="/blog" style={{ color: "rgba(255,255,255,0.62)", fontSize: "14px", fontFamily: ff, textDecoration: "none", transition: "color 0.15s" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#57d7e2")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.62)")}>
              Blog
            </a>
            <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "14px", fontFamily: ff }}>›</span>
            <span style={{ color: "rgba(255,255,255,0.72)", fontSize: "14px", fontFamily: ff }}>{displayLabel}</span>
          </div>

          {/* H1 */}
          <h1 style={{
            color: "white",
            fontSize: "clamp(36px, 4vw, 60px)",
            fontWeight: 800,
            fontFamily: ff,
            margin: "0 0 8px",
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
          }}>{displayLabel}</h1>
          <p style={{ color: "rgba(255,255,255,0.62)", fontSize: "15px", fontFamily: ff, margin: intro ? "0 0 20px" : "0 0 48px" }}>
            {allPosts.length} article{allPosts.length !== 1 ? "s" : ""}
          </p>

          {intro && (
            <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "17px", fontFamily: ff, lineHeight: 1.7, margin: "0 0 48px", maxWidth: "70ch" }}>
              {intro}
            </p>
          )}

          {/* Grid */}
          {pagePosts.length === 0 ? (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <p style={{ color: "rgba(255,255,255,0.66)", fontSize: "18px", fontFamily: ff }}>No posts yet.</p>
              <a href="/blog" style={{ color: "#57d7e2", fontSize: "15px", fontFamily: ff }}>← View all posts</a>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "20px" }}>
              {pagePosts.map((post) => {
                const author = getAuthorBySlug(post.author);
                return (
                  <a key={post.slug} href={`/blog/${post.slug}`} style={{ textDecoration: "none" }}>
                    <div
                      style={{
                        background: "#111",
                        border: "1px solid rgba(255,255,255,0.07)",
                        borderRadius: "14px",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        transition: "border-color 0.2s, transform 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLDivElement).style.borderColor = `${accentColor}55`;
                        (e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(255,255,255,0.07)";
                        (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
                      }}
                    >
                      {/* Cover image */}
                      <div style={{ height: "200px", overflow: "hidden", flexShrink: 0, position: "relative" }}>
                        <CoverImage post={post}
                          imgStyle={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        {hasDiagram(post.slug) && <span className="interactive-badge">Interactive</span>}
                      </div>
                      {/* Content */}
                      <div style={{ padding: "22px", display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}>
                        <h3 style={{
                          color: "white",
                          fontSize: "17px",
                          fontWeight: 700,
                          fontFamily: ff,
                          margin: 0,
                          lineHeight: 1.35,
                        }}>{stripTitleSuffix(post.title)}</h3>
                        {postExcerpt(post) && (
                          <p style={{
                            color: "rgba(255,255,255,0.66)",
                            fontSize: "14px",
                            fontFamily: ff,
                            lineHeight: 1.6,
                            margin: 0,
                            flex: 1,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical" as const,
                            overflow: "hidden",
                          }}>{postExcerpt(post)}</p>
                        )}
                        {/* One author on every post in the corpus, so the
                            avatar row carried no information. */}
                        <div style={{ marginTop: "auto", paddingTop: "12px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                          <span style={{ color: "rgba(255,255,255,0.62)", fontSize: "13px", fontFamily: ff }}>
                            {[relativeDate(post.publishedAt), longReadLabel(post.readingTime)].filter(Boolean).join(" · ")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          )}

          <BlogPagination
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            accentColor={accentColor}
            label={`${displayLabel} articles`}
          />

        </div>
      </div>
    </SiteLayout>
  );
}
